import { useQuery } from "react-query";
import api from "../services/api";

/**
 * Fetch all prerequisites requests for Designer from backend
 * GET /event_uploads/prerequisites_requests?search=<search>&page=<page>&page_size=<page_size>
 */
export const fetchPrerequisitesRequests = async ({ search = "", page = 1, page_size = 10 } = {}) => {
  const params = { page, page_size };
  if (search && search.trim()) {
    params.search = search.trim();
  }
  const res = await api.get("/event_uploads/prerequisites_requests", { params });
  return res.data || { data: [], page: 1, page_size: 10, total: 0, total_pages: 0, has_next: false, has_prev: false };
};

/**
 * Fetch pending prerequisite requests for Admin
 * GET /event_uploads/admin/prerequisites_requests/{event_type}?search=<search>&page=<page>&page_size=<page_size>
 */
export const fetchAdminPendingPrerequisitesRequests = async ({
  eventType = "fdp",
  search = "",
  page = 1,
  page_size = 10
} = {}) => {
  const params = { page, page_size };
  if (search && search.trim()) {
    params.search = search.trim();
  }
  const normalizedType = String(eventType || "fdp").toLowerCase().includes("tech")
    ? "tech_camp"
    : "fdp";
  const res = await api.get(`/event_uploads/admin/prerequisites_requests/${normalizedType}`, { params });
  return res.data || { data: [], page: 1, page_size: 10, total: 0, total_pages: 0, has_next: false, has_prev: false };
};

/**
 * React Query Hook for Prerequisites Requests List
 * Automatically fetches from /event_uploads/admin/prerequisites_requests/{event_type} for Admin
 * and /event_uploads/prerequisites_requests for Designer
 */
export const usePrerequisitesRequests = (
  queryParams = {},
  options = {},
  role = "designer"
) => {
  const isAdmin = role === "admin";
  const { eventType = "FDP", search = "", page = 1, page_size = 10 } = queryParams;

  const queryKey = isAdmin
    ? ["admin-pending-prerequisites-requests", eventType, search, page, page_size]
    : ["prerequisites-requests-list", search, page, page_size];

  const queryFn = () =>
    isAdmin
      ? fetchAdminPendingPrerequisitesRequests({ eventType, search, page, page_size })
      : fetchPrerequisitesRequests({ search, page, page_size });

  return useQuery(queryKey, queryFn, {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 1,
    ...options,
  });
};

/**
 * React Query Hook explicitly for Admin Pending Prerequisites Requests List
 */
export const useAdminPendingPrerequisitesRequests = (
  queryParams = {},
  options = {}
) => {
  return usePrerequisitesRequests(queryParams, options, "admin");
};

export default usePrerequisitesRequests;
